import React, { Component } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  FormControl,
} from '@material-ui/core';
import { withStyles, WithStyles, createStyles, Theme } from '@material-ui/core/styles';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { StatisticsState, PeriodType, InstitutionStatistics } from '../constants/Statistics';

const styles = (theme: Theme) =>
  createStyles({
    container: {
      padding: theme.spacing(3),
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: theme.spacing(3),
      flexWrap: 'wrap',
      gap: theme.spacing(2),
    },
    title: {
      marginRight: theme.spacing(3),
    },
    periodSelector: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    periodSelect: {
      minWidth: 100,
    },
    periodDisplay: {
      padding: theme.spacing(0, 2),
      fontWeight: 500,
    },
    tableContainer: {
      maxHeight: 'calc(100vh - 250px)',
    },
    tableHeaderCell: {
      fontWeight: 'bold',
      backgroundColor: theme.palette.grey[100],
      whiteSpace: 'nowrap',
    },
    tableHeaderGroup: {
      textAlign: 'center',
      borderBottom: `2px solid ${theme.palette.primary.main}`,
    },
    tableCell: {
      whiteSpace: 'nowrap',
    },
    tableCellNumber: {
      textAlign: 'right',
      fontFamily: 'monospace',
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: 200,
    },
    errorMessage: {
      color: theme.palette.error.main,
      padding: theme.spacing(2),
    },
    noDataMessage: {
      textAlign: 'center',
      padding: theme.spacing(4),
      color: theme.palette.text.secondary,
    },
    totalCount: {
      marginTop: theme.spacing(2),
      color: theme.palette.text.secondary,
    },
    disabledInstitution: {
      color: theme.palette.text.disabled,
    },
  });

export interface StateProps {
  auth: any;
  statistics: StatisticsState;
}

export interface DispatchProps {
  statisticsActions: any;
}

export interface OwnProps extends WithStyles<typeof styles> {}

type Props = StateProps & DispatchProps & OwnProps;

class InstitutionUsage extends Component<Props> {
  componentDidMount() {
    this.fetchData();
  }

  fetchData = () => {
    const { periodType, date } = this.props.statistics;
    this.props.statisticsActions.fetchAllInstitutionsStatistics(periodType, date);
  };

  handlePeriodTypeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newPeriodType = event.target.value as PeriodType;
    const newDate = this.props.statisticsActions.changePeriodType(newPeriodType);
    this.props.statisticsActions.fetchAllInstitutionsStatistics(newPeriodType, newDate);
  };

  handlePreviousPeriod = () => {
    const { periodType, date } = this.props.statistics;
    const newDate = this.props.statisticsActions.goToPreviousPeriod(periodType, date);
    this.props.statisticsActions.fetchAllInstitutionsStatistics(periodType, newDate);
  };

  handleNextPeriod = () => {
    const { periodType, date } = this.props.statistics;
    const newDate = this.props.statisticsActions.goToNextPeriod(periodType, date);
    this.props.statisticsActions.fetchAllInstitutionsStatistics(periodType, newDate);
  };

  formatPeriodDisplay = (): string => {
    const { period } = this.props.statistics;
    if (!period) return '';

    const formatDateJP = (dateStr: string) => {
      const [year, month, day] = dateStr.split('-');
      return `${year}/${month}/${day}`;
    };

    return `${formatDateJP(period.from)} 〜 ${formatDateJP(period.to)}`;
  };

  renderTable = () => {
    const { classes, statistics } = this.props;
    const { data, loading, error } = statistics;

    if (loading) {
      return (
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return <Typography className={classes.errorMessage}>エラー: {error}</Typography>;
    }

    if (!data || data.length === 0) {
      return <Typography className={classes.noDataMessage}>データがありません</Typography>;
    }

    return (
      <TableContainer className={classes.tableContainer}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell rowSpan={2} className={classes.tableHeaderCell}>
                施設名
              </TableCell>
              <TableCell colSpan={3} className={`${classes.tableHeaderCell} ${classes.tableHeaderGroup}`}>
                トレーニング実施人数
              </TableCell>
              <TableCell colSpan={2} className={`${classes.tableHeaderCell} ${classes.tableHeaderGroup}`}>
                MoffRaku計画書
              </TableCell>
              <TableCell colSpan={2} className={`${classes.tableHeaderCell} ${classes.tableHeaderGroup}`}>
                科学的介護推進評価
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableHeaderCell}>合計</TableCell>
              <TableCell className={classes.tableHeaderCell}>バンド有</TableCell>
              <TableCell className={classes.tableHeaderCell}>バンド無</TableCell>
              <TableCell className={classes.tableHeaderCell}>作成</TableCell>
              <TableCell className={classes.tableHeaderCell}>更新</TableCell>
              <TableCell className={classes.tableHeaderCell}>作成</TableCell>
              <TableCell className={classes.tableHeaderCell}>更新</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item: InstitutionStatistics) => (
              <TableRow key={item.institution.institution_sub} hover>
                <TableCell className={item.institution.enabled ? classes.tableCell : classes.disabledInstitution}>
                  {item.institution.institution_name}
                  {!item.institution.enabled && ' (無効)'}
                </TableCell>
                <TableCell className={classes.tableCellNumber}>{item.training.total_users}</TableCell>
                <TableCell className={classes.tableCellNumber}>{item.training.with_band_users}</TableCell>
                <TableCell className={classes.tableCellNumber}>{item.training.without_band_users}</TableCell>
                <TableCell className={classes.tableCellNumber}>{item.moffraku_plans.created}</TableCell>
                <TableCell className={classes.tableCellNumber}>{item.moffraku_plans.updated}</TableCell>
                <TableCell className={classes.tableCellNumber}>{item.scientific_nursing_care.created}</TableCell>
                <TableCell className={classes.tableCellNumber}>{item.scientific_nursing_care.updated}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  render() {
    const { classes, statistics } = this.props;
    const { periodType, total, period } = statistics;

    return (
      <Box className={classes.container}>
        <Paper>
          <Box p={3}>
            <Box className={classes.header}>
              <Typography variant="h5" className={classes.title}>
                施設利用状況
              </Typography>

              <Box className={classes.periodSelector}>
                <Typography variant="body2">期間:</Typography>
                <FormControl variant="outlined" size="small">
                  <Select value={periodType} onChange={this.handlePeriodTypeChange} className={classes.periodSelect}>
                    <MenuItem value="weekly">週次</MenuItem>
                    <MenuItem value="monthly">月次</MenuItem>
                  </Select>
                </FormControl>

                <IconButton size="small" onClick={this.handlePreviousPeriod}>
                  <ChevronLeftIcon />
                </IconButton>

                <Typography className={classes.periodDisplay}>{period ? this.formatPeriodDisplay() : '-'}</Typography>

                <IconButton size="small" onClick={this.handleNextPeriod}>
                  <ChevronRightIcon />
                </IconButton>
              </Box>
            </Box>

            {this.renderTable()}

            {total > 0 && (
              <Typography variant="body2" className={classes.totalCount}>
                合計: {total}施設
              </Typography>
            )}
          </Box>
        </Paper>
      </Box>
    );
  }
}

export default withStyles(styles)(InstitutionUsage);
